import { useMemo } from "react";
import { AttributeDefinitionDc } from "@evergis/api";

import { DEFAULT_ID_ATTRIBUTE_NAME, GEOMETRY_ATTRIBUTE } from "../../evergis/constants";
import { layer } from "../../resources/layers";
import { getLayerInfo } from "../../evergis/core/layer/getLayerInfo";
import { useDefaultScaleRange } from "../style/useDefaultScaleRange";
import { LayerInfo } from "../../interfaces/resourceInfo";

export const usePostgresLayer = (layerName: string, options: Omit<Parameters<typeof layer.use>[0], "input"> = {}) => {
  const { data, loading, error } = layer.use({ ...options, input: layerName, waitInput: true });
  const defaultScaleRange = useDefaultScaleRange();

  return useMemo(
    () => ({
      postgresLayer: getLayerInfo(data, defaultScaleRange),
      loading,
      error,
    }),
    [data, defaultScaleRange, loading, error],
  );
};

export const useLayerAttributesFromLayer = (
  layerInfo: LayerInfo,
  excludeBase = false,
): { [key: string]: AttributeDefinitionDc } =>
  useMemo(() => {
    if (!layerInfo?.layerDefinition?.attributes) return {};

    const { attributes, geometryAttribute, idAttribute } = layerInfo.layerDefinition;

    if (excludeBase) {
      delete attributes[geometryAttribute || GEOMETRY_ATTRIBUTE];
      delete attributes[idAttribute || DEFAULT_ID_ATTRIBUTE_NAME];
    }

    return attributes;
  }, [layerInfo, excludeBase]);

export const useLayerAttributes = (
  layerName: string,
  excludeBase = false,
): { [key: string]: AttributeDefinitionDc } => {
  const { postgresLayer } = usePostgresLayer(layerName, { waitInput: true });

  return useLayerAttributesFromLayer(postgresLayer, excludeBase);
};
